import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import JSZip from 'jszip';
import { GlassButton } from '../ui/GlassButton';
import { DownloadIcon } from '../ui/Icons';
/**
 * Highlight differences between two code blocks
 * Shows removed (red), modified (yellow), and added (green) lines
 */
function highlightDifferences(original, fixed) {
    const originalLines = original.split('\n');
    const fixedLines = fixed.split('\n');
    // Create a map of normalized lines for comparison
    const originalMap = new Map();
    originalLines.forEach((line, idx) => {
        const normalized = line.trim();
        if (normalized && !originalMap.has(normalized)) {
            originalMap.set(normalized, idx);
        }
    });
    const fixedMap = new Map();
    fixedLines.forEach((line, idx) => {
        const normalized = line.trim();
        if (normalized && !fixedMap.has(normalized)) {
            fixedMap.set(normalized, idx);
        }
    });
    // Original side: mark removed (red) and unchanged lines
    const originalHighlighted = originalLines.map((line, idx) => {
        const normalized = line.trim();
        const isRemoved = !fixedMap.has(normalized) && normalized.length > 0;
        return (_jsxs("div", { className: `py-1 px-3 rounded flex gap-2 ${isRemoved
                ? 'bg-red-900/40 border-l-4 border-red-600'
                : ''}`, children: [_jsx("span", { className: "text-gray-600 inline-block w-6 flex-shrink-0 text-right", children: idx + 1 }), _jsx("span", { className: isRemoved ? 'text-red-300 line-through flex-1' : 'text-gray-300 flex-1', children: line || ' ' })] }, idx));
    });
    // Fixed side: mark added (green), modified (yellow), and unchanged lines
    const fixedHighlighted = fixedLines.map((line, idx) => {
        const normalized = line.trim();
        const isAdded = !originalMap.has(normalized) && normalized.length > 0;
        const isComment = line.trim().startsWith('//') || line.trim().startsWith('#');
        const isSecurityComment = isComment && (line.includes('SECURITY') || line.includes('FIXED'));
        let bgColor = '';
        let textColor = '';
        let fontWeight = '';
        if (isAdded) {
            bgColor = 'bg-green-900/40 border-l-4 border-green-600';
            textColor = 'text-green-300';
            fontWeight = 'font-semibold';
        }
        else if (isSecurityComment) {
            // Highlight security comments in cyan/blue
            bgColor = 'bg-cyan-900/40 border-l-4 border-cyan-600';
            textColor = 'text-cyan-300';
            fontWeight = 'font-semibold';
        }
        else if (isComment) {
            textColor = 'text-gray-500';
        }
        else {
            // Unchanged lines
            textColor = 'text-gray-300';
        }
        return (_jsxs("div", { className: `py-1 px-3 rounded flex gap-2 ${bgColor}`, children: [_jsx("span", { className: "text-gray-600 inline-block w-6 flex-shrink-0 text-right", children: idx + 1 }), _jsx("span", { className: `${textColor} ${fontWeight} flex-1`, children: line || ' ' })] }, idx));
    });
    return {
        original: _jsx("div", { className: "space-y-0", children: originalHighlighted }),
        fixed: _jsx("div", { className: "space-y-0", children: fixedHighlighted }),
    };
}
// Helper function to check if a file has changes
function hasFileChanges(original, fixed) {
    return original !== fixed;
}
export const FixCodeModal = ({ isOpen, onClose, originalCode, fixedCode, fileName, allFiles = [], currentFileIndex = 0, onFileChange, archiveExtension = '', archiveFileName = '', }) => {
    if (!isOpen)
        return null;
    // Log props for debugging
    console.log('FixCodeModal props:', {
        isOpen,
        fileName,
        archiveExtension,
        allFilesCount: allFiles.length,
        isMultiFile: allFiles.length > 1,
    });
    const isMultiFile = allFiles.length > 1;
    const currentFile = isMultiFile ? allFiles[currentFileIndex] : { originalCode, fixedCode, fileName };
    const hasNext = currentFileIndex < allFiles.length - 1;
    const hasPrev = currentFileIndex > 0;
    const handleNextFile = () => {
        if (hasNext && onFileChange) {
            onFileChange(currentFileIndex + 1);
        }
    };
    const handlePrevFile = () => {
        if (hasPrev && onFileChange) {
            onFileChange(currentFileIndex - 1);
        }
    };
    // Count files with changes for display
    const filesWithChanges = allFiles.filter(f => hasFileChanges(f.originalCode, f.fixedCode)).length;
    const currentFileHasChanges = hasFileChanges(currentFile.originalCode, currentFile.fixedCode);
    const { original: highlightedOriginal, fixed: highlightedFixed } = highlightDifferences(currentFile.originalCode, currentFile.fixedCode);
    const handleDownloadFixed = () => {
        const element = document.createElement('a');
        const fixedFileName = currentFile.fileName.replace(/(\.[^/.]+)?$/, '.fixed$&');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(currentFile.fixedCode));
        element.setAttribute('download', fixedFileName);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };
    const handleDownloadAll = async () => {
        if (!isMultiFile) {
            handleDownloadFixed();
            return;
        }
        try {
            console.log('Starting ZIP creation with JSZip...');
            // Create ZIP instance
            const zip = new JSZip();
            console.log('JSZip instance created');
            // Add each file to the ZIP
            console.log(`Adding ${allFiles.length} files to ZIP...`);
            if (!allFiles || allFiles.length === 0) {
                throw new Error('No files to add to ZIP');
            }
            for (const file of allFiles) {
                console.log(`Adding file: ${file.fileName}`);
                zip.file(file.fileName, file.fixedCode);
            }
            // Add manifest
            const manifest = allFiles
                .map((file, idx) => {
                const isFixed = file.originalCode !== file.fixedCode;
                return `${idx + 1}. ${file.fileName} ${isFixed ? '✓ FIXED' : '(no changes)'}`;
            })
                .join('\n');
            zip.file('FIXES_APPLIED.txt', `SnapV.ai Security Fixes Applied\n${'='.repeat(40)}\n\n${manifest}\n\nAll files are ready to use - no renaming needed!`);
            console.log('Manifest added');
            // Generate ZIP blob
            console.log('Generating ZIP blob...');
            const content = await zip.generateAsync({ type: 'blob' });
            console.log('ZIP blob generated, size:', content.size, 'bytes');
            // Create and trigger download - with safe fallbacks
            const url = URL.createObjectURL(content);
            const safeName = fileName ? String(fileName) : 'archive';
            const baseFileName = safeName.replace(/\.[^.]+$/, '') || 'archive';
            const safeExt = archiveExtension ? String(archiveExtension) : 'zip';
            const filename = `${baseFileName}_SnapV_Fixer.${safeExt}`;
            console.log('Downloading as:', filename);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = filename;
            downloadLink.style.display = 'none';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            // Cleanup
            setTimeout(() => {
                URL.revokeObjectURL(url);
                console.log('Download complete!');
            }, 500);
        }
        catch (error) {
            console.error('ZIP creation error:', error);
            console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace');
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error('Error message:', errorMsg);
            // Show error to user
            alert(`ZIP creation failed: ${errorMsg}\n\nFalling back to individual file downloads...`);
            // Fallback: download files individually
            console.log('Falling back to individual file downloads...');
            if (!allFiles || allFiles.length === 0) {
                alert('No files to download');
                return;
            }
            for (let i = 0; i < allFiles.length; i++) {
                const file = allFiles[i];
                setTimeout(() => {
                    const link = document.createElement('a');
                    link.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(file.fixedCode);
                    link.download = file.fileName;
                    link.click();
                }, i * 300);
            }
        }
    };
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-md", children: _jsxs("div", { className: "relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-white/15 bg-[#0C0C0E] shadow-[0_30px_70px_rgba(0,0,0,0.55)]", children: [_jsxs("div", { className: "pointer-events-none absolute inset-0 opacity-85", children: [_jsx("div", { className: "absolute -left-20 top-8 h-56 w-56 rounded-full bg-blue-500/18 blur-3xl" }), _jsx("div", { className: "absolute -right-20 top-8 h-56 w-56 rounded-full bg-cyan-500/14 blur-3xl" }), _jsx("div", { className: "absolute bottom-[-90px] left-1/3 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" })] }), _jsxs("div", { className: "relative z-10 border-b border-white/10 bg-[linear-gradient(120deg,rgba(11,16,30,0.86),rgba(20,28,42,0.78))] p-6", children: [_jsxs("div", { className: "mb-4 flex items-start justify-between gap-4", children: [_jsxs("div", { className: "flex-1", children: [_jsx("p", { className: "mb-2 inline-flex rounded-full border border-blue-400/35 bg-blue-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-300", children: "Automated Remediation" }), _jsxs("div", { className: "mb-1 flex flex-wrap items-center gap-2", children: [_jsx("h2", { className: "text-2xl font-bold text-white", children: "Fix Preview" }), currentFileHasChanges ? (_jsx("span", { className: "rounded-full border border-emerald-400/35 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-emerald-300", children: "Fixed" })) : (_jsx("span", { className: "rounded-full border border-gray-500/35 bg-gray-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-300", children: "No Changes" }))] }), _jsx("p", { className: "text-sm text-gray-300", children: isMultiFile
                                                ? `File ${currentFileIndex + 1} of ${allFiles.length}: ${currentFile.fileName}`
                                                : currentFile.fileName }), isMultiFile && (_jsxs("p", { className: "mt-1 text-xs text-gray-400", children: [filesWithChanges, " of ", allFiles.length, " files contain applied remediations"] }))] }), _jsx("button", { onClick: onClose, className: "rounded-xl border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm font-semibold text-gray-300 transition hover:border-white/25 hover:text-white", children: "Close" })] }), _jsxs("div", { className: "mb-4 grid grid-cols-1 gap-2 text-xs text-gray-300 sm:grid-cols-3", children: [_jsx("div", { className: "rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2", children: "Removed lines" }), _jsx("div", { className: "rounded-lg border border-cyan-500/25 bg-cyan-500/10 px-3 py-2", children: "Security annotations" }), _jsx("div", { className: "rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-2", children: "Added secure lines" })] }), isMultiFile && (_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("button", { onClick: handlePrevFile, disabled: !hasPrev, className: `rounded-xl px-3 py-1.5 text-sm font-semibold transition ${hasPrev
                                        ? 'border border-blue-400/35 bg-blue-500/12 text-blue-100 hover:bg-blue-500/22'
                                        : 'cursor-not-allowed border border-gray-700/60 bg-gray-700/20 text-gray-500'}`, children: "Previous" }), _jsxs("div", { className: "flex-1 text-center text-xs font-semibold uppercase tracking-[0.08em] text-slate-300", children: [currentFileIndex + 1, " / ", allFiles.length] }), _jsx("button", { onClick: handleNextFile, disabled: !hasNext, className: `rounded-xl px-3 py-1.5 text-sm font-semibold transition ${hasNext
                                        ? 'border border-blue-400/35 bg-blue-500/12 text-blue-100 hover:bg-blue-500/22'
                                        : 'cursor-not-allowed border border-gray-700/60 bg-gray-700/20 text-gray-500'}`, children: "Next" })] }))] }), _jsxs("div", { className: "relative z-10 flex flex-1 flex-col overflow-hidden lg:flex-row", children: [_jsxs("section", { className: "flex-1 overflow-auto border-b border-white/10 p-4 lg:border-b-0 lg:border-r", children: [_jsx("p", { className: "sticky top-0 z-10 mb-3 rounded-lg border border-red-500/20 bg-[#0C0C0E]/95 py-2 text-xs font-semibold uppercase tracking-[0.09em] text-red-300 backdrop-blur-sm", children: "Original (Vulnerable)" }), _jsx("div", { className: "rounded-2xl border border-white/10 bg-black/35 p-2 text-xs font-mono", children: highlightedOriginal })] }), _jsxs("section", { className: "flex-1 overflow-auto p-4", children: [_jsx("p", { className: "sticky top-0 z-10 mb-3 rounded-lg border border-emerald-500/20 bg-[#0C0C0E]/95 py-2 text-xs font-semibold uppercase tracking-[0.09em] text-emerald-300 backdrop-blur-sm", children: "Fixed (Secure)" }), _jsx("div", { className: "rounded-2xl border border-white/10 bg-black/35 p-2 text-xs font-mono", children: highlightedFixed })] })] }), _jsx("div", { className: "relative z-10 border-t border-white/10 bg-black/25 p-5", children: _jsxs("div", { className: "flex flex-col gap-3 sm:flex-row", children: [_jsx(GlassButton, { variant: "secondary", onClick: onClose, className: "flex-1", children: "Close" }), isMultiFile && (_jsxs(GlassButton, { variant: "secondary", onClick: handleDownloadAll, className: "flex flex-1 items-center justify-center gap-2", children: [_jsx(DownloadIcon, { size: 18 }), "Download All Fixed"] })), _jsxs(GlassButton, { variant: "primary", onClick: () => handleDownloadFixed(), className: "flex flex-1 items-center justify-center gap-2", children: [_jsx(DownloadIcon, { size: 18 }), isMultiFile ? 'Download This File' : 'Download Fixed File'] })] }) })] }) }));
};
